import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import { Button, Drawer, IconButton, Stack } from '@mui/material'
// Project imports
import emptyKnowledge from '@/assets/New-images/empty-knowledge.svg'
import loadingimg from '@/assets/images/portal-loading-anim.svg'
import '@/routes/Knowledge/AllKnowledge/AllKnowledge.scss'
import KnowledgeService from '@/services/KnowledgeService'
import { HelperService } from '@/services/helper.service'

const knowledgeService = new KnowledgeService()
const helperService = new HelperService()

interface SharepointUrlsProps {
  groupId: string
}

function SharepointUrls(props: SharepointUrlsProps) {
  const { t } = useTranslation('translation')
  const [isLoadingItems, setIsLoadingItems] = useState(false)
  const [isEditspFolders, setIsEditspFolders] = useState(false)
  const [spFolders, setSpFolders] = useState([])

  // START  SharePoint Settings Logic
  // Only show SharePoint Folders if user is superadmin and api integration with azure is configured
  const fetchSpFolders = async (groupId: string) => {
    setIsLoadingItems(true)
    const res = await knowledgeService.getSpFoldersByGroup(groupId)
    setSpFolders(res?.data?.data?.sp_folders)
    setIsLoadingItems(false)
  }

  useEffect(() => {
    fetchSpFolders(props.groupId)
  }, [props])

  const handleChangeSpf = (value: any, i: any) => {
    const copy = [...spFolders]
    copy[i].url = value
    setSpFolders(copy)
  }

  const handleRemoveSpf = (i: any) => {
    const copy = [...spFolders]
    copy.splice(i, 1)
    setSpFolders(() => [...copy])
  }

  const handleSaveSpfs = async () => {
    if (spFolders.length >= 0) {
      const spFolderPayload: any = spFolders
        .filter(o => o.url !== '')
        .map(o => ({
          sp_folder_id: o._id,
          url: o?.url,
        }))
      try {
        const res = await knowledgeService.saveSpFoldersByGroup(
          props.groupId,
          spFolderPayload,
        )
        setSpFolders(res.data.data.sp_folders)
        helperService.showToaster(t('Success'), 'success')
        setIsEditspFolders(false)
      } catch (error) {
        helperService.showToaster('Error when save SharePoint URLs')
      }
    }
  }

  const renderSpFoldersInput = (value: any, i: any) => {
    // Basic SharePoint URL validation: must start with https and contain .sharepoint.com/
    const sharepointRegex = /^https:\/\/[A-Za-z0-9.-]+\.sharepoint\.com\/.+$/i
    const trimmed = (value || '').trim()
    const isInvalid = trimmed.length > 0 && !sharepointRegex.test(trimmed)

    return (
      <div className="learning-outcome-input">
        <input
          className={`form-control border-0 p-0 ${isInvalid ? 'is-invalid' : ''}`}
          value={value}
          type="url"
          placeholder={t('SharePoint Folder/Page URL')}
          aria-invalid={isInvalid}
          aria-describedby={isInvalid ? `spf-error-${i}` : undefined}
          onChange={e => handleChangeSpf(e.target.value, i)}
          onPaste={e => {
            const pasted = (e.clipboardData.getData('text') || '').trim()
            if (pasted && !/^https:\/\//i.test(pasted)) {
              e.preventDefault()
              handleChangeSpf(`https://${pasted}`, i)
            }
          }}
          // Native pattern fallback (less strict) for browsers
          pattern="https://.*"
          required
        />
        {isInvalid && (
          <small
            id={`spf-error-${i}`}
            className="text-danger d-block mt-1"
            style={{ fontSize: '11px' }}
          >
            {t(
              'Enter a valid SharePoint URL (e.g. https://tenant.sharepoint.com/sites/Folder)',
            )}
          </small>
        )}
        <div
          className="delete-outcome"
          role="button"
          onClick={() => handleRemoveSpf(i)}
          aria-label={t('Remove SharePoint URL')}
        >
          <b className="clr-pink">x</b>
        </div>
      </div>
    )
  }
  // END SharePoint Settings Logic

  return (
    <>
      <Drawer
        open={isEditspFolders}
        anchor="right"
        sx={{ width: '800px' }}
        onClose={() => setIsEditspFolders(false)}
      >
        <div className="tw-p-4 learning tw-w-[800px]">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <h3 className="font-16">{t('Edit SharePoint Folder/Page URLs')}</h3>
            <Button
              disabled={spFolders.length > 0 && spFolders[0].url === ''}
              onClick={() => {
                setSpFolders([{ url: '' }, ...spFolders])
              }}
              sx={{ ml: 'auto' }}
            >
              {t('+ Add')}
            </Button>
            <IconButton onClick={() => setIsEditspFolders(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <div className="">
            {spFolders.length > 0 &&
              spFolders.map((spFolder: any, i: any) =>
                renderSpFoldersInput(spFolder.url, i),
              )}
          </div>
          <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
            <Button variant="contained" onClick={() => handleSaveSpfs()}>
              {t('Save')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setIsEditspFolders(false)}
            >
              {t('Cancel')}
            </Button>
          </Stack>
        </div>
      </Drawer>
      <div className="learning">
        <div className="tw-flex tw-justify-end tw-items-center tw-mb-2">
          <Button
            variant="text"
            onClick={() => {
              setIsEditspFolders(true)
            }}
          >
            {t('+ Add or Edit')}
          </Button>
        </div>

        {spFolders.length === 0 && (
          <div className="empty-knowledge text-center my-2">
            <h3 className="h6 clr-grey-txt7 mb-3">
              {t(
                'You have yet to configure any SharePoint Folder or Page URLs',
              )}
            </h3>
            <figure className="h-auto">
              {isLoadingItems ? (
                <img src={loadingimg} alt="" />
              ) : (
                <img src={emptyKnowledge} alt="" />
              )}
            </figure>
            {!isLoadingItems && (
              <button
                className="btn btn-link m-2"
                onClick={() => {
                  setIsEditspFolders(true)
                }}
              >
                {t('Click here to get started')}
              </button>
            )}
          </div>
        )}

        {spFolders.length > 0 && (
          <div>
            {spFolders
              .filter(
                (spFolder: any) => spFolder.url && spFolder.url.trim() !== '',
              )
              .map((spFolder: any, i: number) => (
                <div className="card learning-outome-item mb-2" key={i}>
                  {spFolder.url}
                </div>
              ))}
          </div>
        )}
      </div>
    </>
  )
}

export default SharepointUrls
